/**
 * Settings sheet.
 *
 * Multi-section settings for the logged-in user. Replaces the
 * legacy "API base URL + bearer token" form (now invisible —
 * the workspace authenticates via the cookie session). Sections:
 *   - Account: display name
 *   - Security: change password, MFA controls, active sessions
 *   - Display: theme, date format
 *   - Workspace defaults: default workflow template, preparer
 *   - Advanced: connection diagnostics + override (rarely used)
 */

import { useEffect, useMemo, useState } from "react";
import { Icon } from "../icons";
import {
  getHealth,
  getVersion,
  loadConfig,
  saveConfig,
  type ApiError,
} from "../api/client";
import {
  authApi,
  type AuthApiError,
} from "../auth/api";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../Toasts";

const MIN_PASSWORD_LENGTH = 12;

type Theme = "system" | "light" | "dark";
type DateFormat = "iso" | "dmy" | "mdy";
type WorkflowDefault = "variation" | "site_instruction";

interface UserPrefs {
  theme: Theme;
  dateFormat: DateFormat;
  workflowDefault: WorkflowDefault;
  preparerOverride: string;
}

const DEFAULT_PREFS: UserPrefs = {
  theme: "system",
  dateFormat: "dmy",
  workflowDefault: "variation",
  preparerOverride: "",
};

function loadPrefs(): UserPrefs {
  try {
    const raw = localStorage.getItem("cvp.prefs");
    if (raw === null) {
      return DEFAULT_PREFS;
    }
    const parsed = JSON.parse(raw) as Partial<UserPrefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: UserPrefs): void {
  localStorage.setItem("cvp.prefs", JSON.stringify(prefs));
  applyTheme(prefs.theme);
}

function applyTheme(theme: Theme): void {
  // Explicit theme sets the data-theme attribute the CSS uses to
  // override the default tokens. "system" clears it so the OS
  // preference takes effect through prefers-color-scheme media
  // queries.
  if (theme === "system") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", theme);
  }
}

export function SettingsView() {
  const toast = useToast();
  const { principal, refresh } = useAuth();
  const [prefs, setPrefs] = useState<UserPrefs>(() => loadPrefs());

  // Apply theme on first render so the user sees their preference
  // applied even before the form is touched.
  useEffect(() => {
    applyTheme(prefs.theme);
  }, [prefs.theme]);

  const onPrefChange = <K extends keyof UserPrefs>(key: K, value: UserPrefs[K]) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    savePrefs(next);
    toast.show("ok", "Preference saved.");
  };

  return (
    <div className="settings-page" data-testid="settings-view">
      <div className="out-eyebrow">Workspace</div>
      <h1 className="out-title" style={{ fontSize: 22, marginTop: 4 }}>
        Settings
      </h1>

      {principal !== null && (
        <>
          <AccountSection refresh={refresh} />
          <SecuritySection refresh={refresh} mfaEnrolled={principal.mfa_enrolled} />
        </>
      )}

      <SettingsSection title="Display">
        <Field label="Theme">
          <select
            value={prefs.theme}
            onChange={(e) => onPrefChange("theme", e.target.value as Theme)}
          >
            <option value="system">System (follow OS)</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </Field>
        <Field label="Date format">
          <select
            value={prefs.dateFormat}
            onChange={(e) =>
              onPrefChange("dateFormat", e.target.value as DateFormat)
            }
          >
            <option value="dmy">DD/MM/YYYY (Australian)</option>
            <option value="mdy">MM/DD/YYYY (US)</option>
            <option value="iso">YYYY-MM-DD (ISO 8601)</option>
          </select>
        </Field>
      </SettingsSection>

      <SettingsSection title="Workspace defaults">
        <Field label="Default workflow template">
          <select
            value={prefs.workflowDefault}
            onChange={(e) =>
              onPrefChange("workflowDefault", e.target.value as WorkflowDefault)
            }
          >
            <option value="variation">Variation</option>
            <option value="site_instruction">Site instruction</option>
          </select>
        </Field>
        <Field
          label="Preparer attribution (overrides default)"
          hint="Appears on Briefing Note and Determination Letter PDFs. Leave blank to use the platform default."
        >
          <input
            type="text"
            value={prefs.preparerOverride}
            placeholder="e.g. Alex Harper"
            onChange={(e) =>
              setPrefs((current) => ({ ...current, preparerOverride: e.target.value }))
            }
            onBlur={() => onPrefChange("preparerOverride", prefs.preparerOverride)}
          />
        </Field>
      </SettingsSection>

      <AdvancedSection toast={toast} />
    </div>
  );
}

function SettingsSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="settings-section">
      <h2 className="settings-section-title">{title}</h2>
      <div className="settings-section-body">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint !== undefined && <div className="field-hint">{hint}</div>}
    </div>
  );
}

function AccountSection({ refresh }: { refresh: () => Promise<void> }) {
  const toast = useToast();
  const { principal } = useAuth();
  const [displayName, setDisplayName] = useState(principal?.display_name ?? "");
  const [busy, setBusy] = useState(false);

  // Keep the form in sync if the principal updates from elsewhere.
  useEffect(() => {
    setDisplayName(principal?.display_name ?? "");
  }, [principal?.display_name]);

  async function onSave() {
    setBusy(true);
    try {
      await authApi.updateMe(displayName.trim() || null);
      await refresh();
      toast.show("ok", "Profile updated.");
    } catch (exc) {
      const apiErr = exc as AuthApiError;
      toast.show("err", apiErr.message ?? "Could not update profile.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSection title="Account">
      <Field
        label="Email"
        hint="Email changes are not yet supported. Contact support if you need one."
      >
        <input type="email" value={principal?.email ?? ""} disabled readOnly />
      </Field>
      <Field label="Display name">
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="How the workspace addresses you"
          maxLength={120}
        />
      </Field>
      <div className="actions">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void onSave()}
          disabled={busy}
        >
          {busy ? "Saving..." : "Save profile"}
        </button>
      </div>
    </SettingsSection>
  );
}

function SecuritySection({
  refresh,
  mfaEnrolled,
}: {
  refresh: () => Promise<void>;
  mfaEnrolled: boolean;
}) {
  const toast = useToast();
  return (
    <SettingsSection title="Security">
      <ChangePasswordCard toast={toast} />
      <MfaCard toast={toast} mfaEnrolled={mfaEnrolled} refresh={refresh} />
      <ActiveSessionsCard toast={toast} />
    </SettingsSection>
  );
}

function ChangePasswordCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm) {
      toast.show("err", "New passwords do not match.");
      return;
    }
    if (next.length < MIN_PASSWORD_LENGTH) {
      toast.show("err", `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword(current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.show("ok", "Password changed. Other sessions have been signed out.");
    } catch (exc) {
      const apiErr = exc as AuthApiError;
      toast.show(
        "err",
        apiErr.status === 401
          ? "Current password is incorrect."
          : (apiErr.message ?? "Could not change password."),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="settings-card" onSubmit={onSubmit} noValidate>
      <h3 className="settings-card-title">Change password</h3>
      <Field label="Current password">
        <input
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
        />
      </Field>
      <Field label="New password">
        <input
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
        />
      </Field>
      <Field label="Confirm new password">
        <input
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
      </Field>
      <div className="actions">
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {busy ? "Updating..." : "Change password"}
        </button>
      </div>
      <p className="field-hint">
        Other sessions on other devices will be signed out automatically.
      </p>
    </form>
  );
}

function MfaCard({
  toast,
  mfaEnrolled,
  refresh,
}: {
  toast: ReturnType<typeof useToast>;
  mfaEnrolled: boolean;
  refresh: () => Promise<void>;
}) {
  const [showRegen, setShowRegen] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [code, setCode] = useState("");
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function onRegenerate() {
    setBusy(true);
    try {
      const result = await authApi.regenerateRecoveryCodes();
      setNewCodes(result.recovery_codes);
      setShowRegen(false);
    } catch (exc) {
      const apiErr = exc as AuthApiError;
      toast.show("err", apiErr.message ?? "Could not regenerate codes.");
    } finally {
      setBusy(false);
    }
  }

  async function onReset(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.resetMfa(code);
      setCode("");
      setShowReset(false);
      await refresh();
      toast.show("ok", "MFA cleared. Visit /mfa/enrol to set up again.");
    } catch (exc) {
      const apiErr = exc as AuthApiError;
      toast.show(
        "err",
        apiErr.status === 401
          ? "That code is not valid."
          : (apiErr.message ?? "Could not reset MFA."),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-card">
      <h3 className="settings-card-title">Two-factor authentication</h3>
      {mfaEnrolled ? (
        <>
          <p className="field-hint">
            MFA is active. You will be prompted for a code on every sign-in.
          </p>
          <div className="actions">
            {!showRegen && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setShowRegen(true)}
              >
                Regenerate recovery codes
              </button>
            )}
            {!showReset && (
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => setShowReset(true)}
              >
                Reset MFA
              </button>
            )}
          </div>
          {showRegen && (
            <div className="settings-confirm">
              <p>
                Existing recovery codes will be invalidated and replaced.
                Continue?
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => void onRegenerate()}
                  disabled={busy}
                >
                  Yes, regenerate
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setShowRegen(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {showReset && (
            <form onSubmit={onReset} className="settings-confirm" noValidate>
              <p>
                Enter your current authenticator code to confirm. After reset
                you will need to enrol again before your next sign-in.
              </p>
              <Field label="Current 6-digit code">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </Field>
              <div className="actions">
                <button
                  type="submit"
                  className="btn btn-danger btn-sm"
                  disabled={busy}
                >
                  Reset MFA
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => {
                    setShowReset(false);
                    setCode("");
                  }}
                  disabled={busy}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {newCodes !== null && (
            <div className="settings-recovery">
              <p>
                Save these one-time recovery codes. They replace any older
                codes you may have stored.
              </p>
              <ul className="auth-recovery-codes">
                {newCodes.map((c) => (
                  <li key={c}>
                    <code>{c}</code>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setNewCodes(null)}
              >
                I have saved these
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="field-hint">
            MFA is not yet set up. Strongly recommended for any account that
            can edit data.
          </p>
          <div className="actions">
            <a className="btn btn-primary btn-sm" href="/mfa/enrol">
              Set up MFA
            </a>
          </div>
        </>
      )}
    </div>
  );
}

interface SessionItem {
  id: string;
  issued_at: string;
  last_seen_at: string;
  expires_at: string;
  issued_user_agent: string | null;
  issued_ip: string | null;
  is_current: boolean;
}

function ActiveSessionsCard({ toast }: { toast: ReturnType<typeof useToast> }) {
  const [sessions, setSessions] = useState<SessionItem[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const formatDate = useMemo(
    () => (iso: string) => {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },
    [],
  );

  async function reload() {
    try {
      const result = await authApi.listSessions();
      setSessions(result.sessions);
    } catch {
      setSessions([]);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onRevoke(id: string) {
    setBusyId(id);
    try {
      await authApi.revokeSession(id);
      await reload();
      toast.show("ok", "Session revoked.");
    } catch (exc) {
      const apiErr = exc as AuthApiError;
      toast.show("err", apiErr.message ?? "Could not revoke session.");
    } finally {
      setBusyId(null);
    }
  }

  async function onRevokeAll() {
    setBusyId("__all__");
    try {
      await authApi.revokeAllSessions();
      await reload();
      toast.show("ok", "Other devices signed out.");
    } catch (exc) {
      const apiErr = exc as AuthApiError;
      toast.show("err", apiErr.message ?? "Could not sign out other devices.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="settings-card">
      <h3 className="settings-card-title">Active sessions</h3>
      {sessions === null ? (
        <p className="field-hint">Loading...</p>
      ) : sessions.length === 0 ? (
        <p className="field-hint">No active sessions.</p>
      ) : (
        <ul className="session-list">
          {sessions.map((s) => (
            <li
              key={s.id}
              className={`session-row${s.is_current ? " session-row-current" : ""}`}
            >
              <div className="session-meta">
                <div className="session-ua">
                  {s.issued_user_agent ?? "Unknown device"}
                </div>
                <div className="session-detail">
                  Last used {formatDate(s.last_seen_at)}
                  {s.issued_ip !== null && ` · ${s.issued_ip}`}
                  {s.is_current && " · this device"}
                </div>
              </div>
              {!s.is_current && (
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={busyId === s.id}
                  onClick={() => void onRevoke(s.id)}
                >
                  {busyId === s.id ? "..." : "Revoke"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {sessions !== null && sessions.length > 1 && (
        <div className="actions" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            disabled={busyId === "__all__"}
            onClick={() => void onRevokeAll()}
          >
            {busyId === "__all__" ? "..." : "Sign out other devices"}
          </button>
        </div>
      )}
    </div>
  );
}

function AdvancedSection({ toast }: { toast: ReturnType<typeof useToast> }) {
  const [cfg, setCfg] = useState(() => loadConfig());
  const [version, setVersion] = useState<string>("");
  const [open, setOpen] = useState(false);

  const onSave = () => {
    saveConfig(cfg);
    toast.show("ok", "Connection settings saved.");
  };

  const onTest = async () => {
    try {
      const health = await getHealth(cfg);
      const ver = await getVersion(cfg);
      setVersion(`${ver.name} ${ver.version} (${ver.api_version})`);
      toast.show("ok", `Connection ok. Server time ${health.timestamp}.`);
    } catch (err) {
      const apiErr = err as ApiError;
      setVersion("");
      toast.show("err", `Connection failed: ${apiErr.message ?? "unknown"}`);
    }
  };

  return (
    <SettingsSection title="Advanced">
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setOpen((current) => !current)}
        style={{ alignSelf: "flex-start" }}
      >
        {open ? "Hide" : "Show"} connection diagnostics
      </button>
      {open && (
        <div className="settings-card">
          <p className="field-hint">
            Most users do not need to change these. The workspace authenticates
            via your sign-in cookie. The bearer token below is only consulted
            for API/MCP clients without a session.
          </p>
          <Field label="API base URL">
            <input
              // Deliberately "text", not "url". A url-typed input asks
              // for an absolute address, and an absolute cross-origin
              // address is the one value that cannot work here: the
              // API mounts no CORS middleware because the same-origin
              // proxy makes it unnecessary, so the browser refuses
              // every request and the whole workspace fails with
              // "Failed to fetch". The client now discards such a
              // value (see normaliseBaseUrl), and the field no longer
              // invites it.
              type="text"
              value={cfg.baseUrl}
              onChange={(e) => setCfg({ ...cfg, baseUrl: e.target.value })}
              placeholder="/api"
            />
            <p className="field-hint">
              Leave this as <span className="mono">/api</span>. The workspace
              reaches the API through a same-origin proxy; a full address
              such as <span className="mono">https://…</span> cannot work and
              is ignored.
            </p>
          </Field>
          <Field label="Service-account bearer token (optional)">
            <input
              type="password"
              value={cfg.token}
              onChange={(e) => setCfg({ ...cfg, token: e.target.value })}
            />
          </Field>
          <div className="actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={onSave}>
              Save
            </button>
            <button type="button" className="btn btn-sm" onClick={() => void onTest()}>
              Test connection
            </button>
          </div>
          {version && (
            <p className="field-hint">
              Connected to <span className="mono">{version}</span>.
            </p>
          )}
        </div>
      )}
    </SettingsSection>
  );
}

export function SettingsSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="sheet-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Settings"
      onClick={onClose}
    >
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <div className="sheet-title">Settings</div>
          <button
            className="btn-icon"
            type="button"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon.cross />
          </button>
        </div>
        <div className="sheet-body">
          <SettingsView />
        </div>
      </div>
    </div>
  );
}
